import { EventContextProvider, useAnalytics } from "@codat/analytics";
import {
  Button,
  Card,
  TextInput,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import {
  useResendVerifyLoginEmailMutation,
  useVerifyLoginMutation,
} from "api/verifyLogin";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { sanitizeRedirect } from "utils/sanitizeRedirect";
import { useQuery } from "utils/useQuery";
import { useTitle } from "utils/useTitle";

import AuthPage from "components/AuthPage";

import styles from "./VerifyEmail.module.css";

const VerifyEmail: React.FC = () => {
  const query = useQuery();
  const email = query.get("email");
  const returnUrl = query.get("returnUrl");
  const sanitizedRedirect = sanitizeRedirect(returnUrl);

  const [otp, setOtp] = useState("");
  useTitle("Verify Email");
  const { sendPageViewEvent } = useAnalytics();
  const history = useHistory();
  const { addToast } = useToast();
  const [{ loading, error: verificationError }, verifyOtp] =
    useVerifyLoginMutation();
  const [, resendEmail] = useResendVerifyLoginEmailMutation();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await verifyOtp({
        data: {
          verificationId: query.get("verificationId") ?? "",
          verificationCode: otp,
        },
      });
      sanitizedRedirect
        ? window.location.replace(sanitizedRedirect)
        : history.push(`/signin?emailVerified=true`);
    } catch (error) {
      addToast({
        variant: "error",
        message: "Failed to verify sign-in. Please try again.",
      });
    }
  };

  const handleResendEmail = async () => {
    try {
      await resendEmail({
        data: {
          verificationId: query.get("verificationId") ?? "",
        },
      });
      addToast({
        variant: "success",
        message: `Email resent to ${email}`,
      });
    } catch (error) {
      addToast({
        variant: "error",
        message: "Failed to resend email. Please try again.",
      });
    }
  };

  useEffect(() => {
    if (!email) {
      throw Error("Invalid url, email must be provided");
    }
  }, [email]);

  const otpTooLong = otp.length > 50;

  useEffect(() => {
    sendPageViewEvent("Verify Email");
  }, [sendPageViewEvent]);

  return (
    <AuthPage>
      <Card className={styles.verifyEmailCard} data-testid="verify-email-card">
        <form className={styles.verifyEmailContent} onSubmit={onSubmit}>
          <div className={styles.verifyEmailText}>
            <Typography variant="heading_xl" as="h1" data-testid="heading">
              You&apos;re using a new sign-in method...
            </Typography>
            <div className={styles.textBody}>
              <Typography variant="p" as="p">
                We haven&apos;t seen you sign in like this before, so
                you&apos;ll need to{" "}
                <span className={styles.bold}>verify this sign-in method</span>.
                Don&apos;t worry, you&apos;ll only have to do this once!
              </Typography>
              <div>
                <Typography variant="p" as="p">
                  <span className={styles.bold}>
                    We have sent a one-time password to:
                  </span>
                </Typography>
                <Typography
                  variant="p"
                  as="p"
                  className={styles.noMargin}
                  data-testid="email"
                >
                  {email}
                </Typography>
              </div>
              <Typography variant="p" as="p">
                This code will expire in 10 minutes.
              </Typography>
            </div>
          </div>
          <TextInput
            data-testid="otp-input"
            value={otp}
            label="One-time password"
            onChange={(event) => setOtp(event.target.value)}
            className={styles.input}
            error={otpTooLong || !!verificationError}
            errorMessage={
              otpTooLong
                ? "One-time password is too long"
                : verificationError?.response?.data?.error
            }
          />
          <Button
            className={styles.verifyButton}
            data-testid="otp-verify-button"
            label="Verify sign-in method"
            type="submit"
            disabled={otp.length === 0 || otpTooLong}
            isLoading={loading}
          />
        </form>
        <div className={styles.resendEmailWrapper}>
          <Typography as="p" variant="p">
            Not recieved an email?
          </Typography>
          <Button
            data-testid="resend-button"
            variant="tertiary"
            label="Resend"
            size="small"
            onClick={handleResendEmail}
          />
        </div>
      </Card>
    </AuthPage>
  );
};

export default function VerifyEmailWithTracking() {
  return (
    <EventContextProvider name="Verify Email">
      <VerifyEmail />
    </EventContextProvider>
  );
}
